import React, { useEffect, useState } from 'react';
import { Loader2, Settings2 } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../api';

/**
 * BD-13 — the firm's pipeline review settings (administrators). Ryan's open
 * questions are settings here: weekly or biweekly, who sets ranks 1–7, and which
 * Zoho field holds the rank. Saved with one button (PUT /api/bd/pipeline/settings).
 */

export interface PipelineSettings {
  meeting_name: string;
  cadence: 'weekly' | 'biweekly';
  meeting_weekday: number;
  meeting_time: string;
  prep_timing: 'evening_before' | 'morning_of';
  participant_user_ids: number[];
  rank_field: string | null;
  on_deck_value: string;
  rank_setter: 'owner' | 'partner';
  stale_days: number;
  timezone: string;
  partner_user_id: number | null;
}

// The firm's zones; a saved zone not in this list is still shown (added below).
const ZONES = ['America/New_York', 'America/Chicago', 'America/Denver', 'America/Phoenix', 'America/Los_Angeles', 'Europe/London', 'Asia/Kolkata', 'UTC'];

interface Person { id: number; name: string; email: string; zoho_mapped: boolean }

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const INPUT = 'w-full rounded-lg border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500';

const PipelineSettingsCard: React.FC<{ onSaved?: (s: PipelineSettings) => void }> = ({ onSaved }) => {
  const [s, setS] = useState<PipelineSettings | null>(null);
  const [people, setPeople] = useState<Person[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [{ data }, ov] = await Promise.all([
          api.get('/api/bd/pipeline/settings'),
          api.get('/api/bd-assistant/admin/overview').catch(() => ({ data: { eligible: [] } })),
        ]);
        setS(data.settings);
        setPeople((ov.data.eligible || []).map((e: any) => ({ id: e.user.id, name: e.user.name, email: e.user.email, zoho_mapped: Boolean(e.capabilities?.zoho_mapped) })));
      } catch {
        toast.error('Could not load the pipeline settings');
      }
    })();
  }, []);

  if (!s) return <div className="text-sm text-gray-400 p-4">Loading settings…</div>;
  const set = (patch: Partial<PipelineSettings>) => setS({ ...s, ...patch });
  const toggle = (id: number) => set({
    participant_user_ids: s.participant_user_ids.includes(id) ? s.participant_user_ids.filter((x) => x !== id) : [...s.participant_user_ids, id],
  });

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put('/api/bd/pipeline/settings', { ...s, rank_field: s.rank_field || null });
      setS(data.settings);
      toast.success('Pipeline settings saved');
      if (onSaved) onSaved(data.settings);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Could not save the settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-lg border border-gray-200 p-4 space-y-4" data-testid="bd-pipeline-settings">
      <div className="flex items-center gap-2 text-sm font-semibold text-gray-900"><Settings2 className="w-4 h-4" /> Pipeline review settings</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <label className="text-sm">Meeting name
          <input className={INPUT} value={s.meeting_name} maxLength={120} onChange={(e) => set({ meeting_name: e.target.value })} data-testid="bd-pipeline-name" />
        </label>
        <label className="text-sm">Cadence
          <select className={INPUT} value={s.cadence} onChange={(e) => set({ cadence: e.target.value as PipelineSettings['cadence'] })} data-testid="bd-pipeline-cadence">
            <option value="weekly">Weekly</option>
            <option value="biweekly">Every two weeks</option>
          </select>
        </label>
        <label className="text-sm">Meeting day
          <select className={INPUT} value={s.meeting_weekday} onChange={(e) => set({ meeting_weekday: Number(e.target.value) })}>
            {DAYS.map((d, i) => <option key={d} value={i}>{d}</option>)}
          </select>
        </label>
        <label className="text-sm">Meeting time
          <input type="time" className={INPUT} value={s.meeting_time} onChange={(e) => set({ meeting_time: e.target.value })} />
        </label>
        <label className="text-sm">Meeting time zone
          <select className={INPUT} value={s.timezone} onChange={(e) => set({ timezone: e.target.value })} data-testid="bd-pipeline-timezone">
            {(ZONES.includes(s.timezone) ? ZONES : [s.timezone, ...ZONES]).map((z) => <option key={z} value={z}>{z}</option>)}
          </select>
        </label>
        <label className="text-sm">Prepare and send in Slack
          <select className={INPUT} value={s.prep_timing} onChange={(e) => set({ prep_timing: e.target.value as PipelineSettings['prep_timing'] })}>
            <option value="evening_before">The evening before (17:00)</option>
            <option value="morning_of">The morning of (07:00)</option>
          </select>
          <span className="text-xs text-gray-500">In each person's own time zone. Never after the meeting starts.</span>
        </label>
        <label className="text-sm">Partner
          <select className={INPUT} value={s.partner_user_id ?? ''} onChange={(e) => set({ partner_user_id: e.target.value ? Number(e.target.value) : null })} data-testid="bd-pipeline-partner">
            <option value="">No partner</option>
            {people.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <span className="text-xs text-gray-500">Gets the partner questions and reviews changes on deals with no Jarvis owner. None: those stay in this tab and the pack.</span>
        </label>
        <label className="text-sm">No activity counts as stale after (days)
          <input type="number" min={1} max={365} className={INPUT} value={s.stale_days} onChange={(e) => set({ stale_days: Number(e.target.value) })} />
        </label>
        <label className="text-sm">Zoho field holding the rank (1–7)
          <input className={INPUT} placeholder="e.g. Pipeline_Rank" value={s.rank_field || ''} onChange={(e) => set({ rank_field: e.target.value })} data-testid="bd-pipeline-rank-field" />
          <span className="text-xs text-gray-500">The field's API name in Zoho Deals. Empty: every deal shows as {s.on_deck_value}.</span>
        </label>
        <label className="text-sm">Shown when no rank is set
          <input className={INPUT} maxLength={40} value={s.on_deck_value} onChange={(e) => set({ on_deck_value: e.target.value })} />
        </label>
        <label className="text-sm">Who sets ranks
          <select className={INPUT} value={s.rank_setter} onChange={(e) => set({ rank_setter: e.target.value as PipelineSettings['rank_setter'] })}>
            <option value="owner">Each deal owner</option>
            <option value="partner">A partner</option>
          </select>
        </label>
      </div>
      <div>
        <div className="text-sm font-medium text-gray-900">Participants</div>
        <p className="text-xs text-gray-500 mb-1">Their open deals are reviewed. None selected: everyone mapped to a Zoho user. Deals whose owner is not a Jarvis user are always included, with a "who owns this?" question.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
          {people.map((p) => (
            <label key={p.id} className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={s.participant_user_ids.includes(p.id)} onChange={() => toggle(p.id)} />
              <span>{p.name}</span>
              {!p.zoho_mapped && <span className="text-xs text-amber-600">not mapped to Zoho</span>}
            </label>
          ))}
          {!people.length && <span className="text-xs text-gray-400">No eligible people found.</span>}
        </div>
      </div>
      <button type="button" onClick={save} disabled={saving} className="text-sm px-3 py-1.5 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 inline-flex items-center gap-1.5" data-testid="bd-pipeline-save">
        {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Save settings
      </button>
    </div>
  );
};

export default PipelineSettingsCard;
